import React, { useMemo, useState } from 'react';
import { ArrowLeftIcon, PlusIcon, TrashIcon, RepeatIcon, CalendarIcon, AlertTriangleIcon, CheckIcon, LockIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { ActivityResources } from './ActivityResources';
import { RESOURCES, BOOKING_FILES } from '../../data/windsor';

/** Venues an activity can be scheduled in. */
const FACILITIES = RESOURCES.filter(r => r.kind === 'plateau' && r.bookable).map(r => ({
  id: r.id,
  name: r.name,
  occupancy: r.occupancy ?? 0
}));
import type { Activity, ActivityResource, ActivitySlot, Prerequisite } from '../../types/loisirs';
interface Props {
  /** Activity being edited, or undefined when creating a new one. */
  activity?: Activity;
  /** Every activity already scheduled, used for conflict detection. */
  existing: Activity[];
  onCancel: () => void;
  onSave: (activity: Activity) => void;
}
const SEASONS = [{
  fr: 'Hiver 2026',
  en: 'Winter 2026'
}, {
  fr: 'Printemps 2026',
  en: 'Spring 2026'
}, {
  fr: 'Été 2026',
  en: 'Summer 2026'
}, {
  fr: 'Automne 2026',
  en: 'Fall 2026'
}];
const WEEKDAYS = [{
  value: 1,
  fr: 'Lundi',
  en: 'Monday'
}, {
  value: 2,
  fr: 'Mardi',
  en: 'Tuesday'
}, {
  value: 3,
  fr: 'Mercredi',
  en: 'Wednesday'
}, {
  value: 4,
  fr: 'Jeudi',
  en: 'Thursday'
}, {
  value: 5,
  fr: 'Vendredi',
  en: 'Friday'
}, {
  value: 6,
  fr: 'Samedi',
  en: 'Saturday'
}, {
  value: 7,
  fr: 'Dimanche',
  en: 'Sunday'
}];

/** Prerequisite catalogue enabled in the Configure tab. */
const PREREQUISITE_CATALOGUE: Prerequisite[] = [{
  id: 'cat-age',
  kind: 'age',
  label: {
    fr: 'Tranche d’âge respectée',
    en: 'Age range respected'
  },
  detail: {
    fr: 'Calculée au 31 décembre de l’année de la session.',
    en: 'Calculated as of December 31 of the session year.'
  },
  blocking: true
}, {
  id: 'cat-level',
  kind: 'level',
  label: {
    fr: 'Niveau précédent réussi',
    en: 'Previous level passed'
  },
  detail: {
    fr: 'Vérifie l’historique d’inscription du citoyen.',
    en: 'Checks the citizen’s registration history.'
  },
  blocking: true
}, {
  id: 'cat-residency',
  kind: 'residency',
  label: {
    fr: 'Résident de Windsor',
    en: 'Windsor resident'
  },
  detail: {
    fr: 'Les non-résidents sont acceptés après la période de priorité.',
    en: 'Non-residents are accepted after the priority window.'
  },
  blocking: false
}, {
  id: 'cat-document',
  kind: 'document',
  label: {
    fr: 'Certificat médical',
    en: 'Medical certificate'
  },
  detail: {
    fr: 'À téléverser à l’inscription, valide depuis moins de 12 mois.',
    en: 'Uploaded at registration, issued within the last 12 months.'
  },
  blocking: true
}];
const toDecimal = (time: string) => {
  const [h, m] = time.split(':').map(Number);
  return h + m / 60;
};

/** Loose venue match: facilities and activities name places differently. */
function sameVenue(a: string, b: string) {
  const key = (value: string) => value.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').split(/[—\-,]/).map(part => part.trim()).filter(Boolean);
  return key(a).some(pa => key(b).some(pb => pa.includes(pb) || pb.includes(pa)));
}
export function ActivityForm({
  activity,
  existing,
  onCancel,
  onSave
}: Props) {
  const {
    t,
    lang
  } = useLanguage();
  const editing = Boolean(activity);
  const [typeFr, setTypeFr] = useState(activity?.type.fr ?? '');
  const [typeEn, setTypeEn] = useState(activity?.type.en ?? '');
  const [nameFr, setNameFr] = useState(activity?.name.fr ?? '');
  const [nameEn, setNameEn] = useState(activity?.name.en ?? '');
  const [venueId, setVenueId] = useState(FACILITIES.find(f => activity && sameVenue(f.name.fr, activity.venue.fr))?.id ?? FACILITIES[0].id);
  const [season, setSeason] = useState(activity?.season.fr ?? SEASONS[0].fr);
  const [recurrence, setRecurrence] = useState<'weekly' | 'once'>(activity?.recurrence ?? 'weekly');
  const [slots, setSlots] = useState<ActivitySlot[]>(activity?.slots ?? [{
    id: `sl-${Date.now()}`,
    weekday: 1,
    startTime: '18:00',
    endTime: '19:00'
  }]);
  const [ageMin, setAgeMin] = useState(activity?.ageRange.split('–')[0]?.trim() ?? '');
  const [ageMax, setAgeMax] = useState(activity?.ageRange.split('–')[1]?.trim() ?? '');
  const [capacity, setCapacity] = useState(String(activity?.capacity ?? ''));
  const [priceResident, setPriceResident] = useState(String(activity?.priceResident ?? ''));
  const [priceNonResident, setPriceNonResident] = useState(String(activity?.priceNonResident ?? ''));
  const [prerequisites, setPrerequisites] = useState<Prerequisite[]>(activity?.prerequisites ?? []);
  const [visible, setVisible] = useState(activity?.visible ?? true);
  const [resources, setResources] = useState<ActivityResource[]>(activity?.resources ?? []);
  const venue = FACILITIES.find(f => f.id === venueId) ?? FACILITIES[0];
  const known = useMemo(() => {
    const map = new Map<string, {
      fr: string;
      en: string;
    }>();
    existing.forEach(a => map.set(a.type.fr, {
      fr: a.type.fr,
      en: a.type.en
    }));
    return Array.from(map.values());
  }, [existing]);

  /** Bookings, activities and maintenance already sitting on the chosen venue. */
  const conflicts = useMemo(() => {
    const found: string[] = [];
    slots.forEach(slot => {
      const start = toDecimal(slot.startTime);
      const end = toDecimal(slot.endTime);
      if (end <= start) return;
      existing.filter(a => a.id !== activity?.id && sameVenue(a.venue.fr, venue.name.fr)).forEach(a => a.slots.forEach(other => {
        if (other.weekday !== slot.weekday) return;
        if (toDecimal(other.startTime) < end && toDecimal(other.endTime) > start) {
          found.push(t(`${a.name.fr} occupe déjà ce créneau`, `${a.name.en} already occupies this slot`));
        }
      }));
      BOOKING_FILES.filter(b => b.status !== 'annulee' && b.resourceId === venue.id).forEach(b => {
        b.slots.forEach(booked => {
          const day = new Date(`${booked.date}T12:00:00`).getDay();
          const weekday = day === 0 ? 7 : day;
          if (weekday !== slot.weekday) return;
          if (toDecimal(booked.startTime) < end && toDecimal(booked.endTime) > start) {
            found.push(t(`Réservation de ${b.client.name} sur ce créneau`, `${b.client.name} has a booking on this slot`));
          }
        });
      });
    });
    return Array.from(new Set(found));
  }, [slots, venue, existing, activity, t]);
  const invalidSlot = slots.some(s => toDecimal(s.endTime) <= toDecimal(s.startTime));
  const complete = nameFr.trim() && typeFr.trim() && capacity.trim() && priceResident.trim() && !invalidSlot;
  const submit = () => {
    if (!complete) return;
    onSave({
      id: activity?.id ?? `act-${Date.now()}`,
      type: {
        fr: typeFr.trim(),
        en: (typeEn || typeFr).trim()
      },
      name: {
        fr: nameFr.trim(),
        en: (nameEn || nameFr).trim()
      },
      venue: venue.name,
      recurrence,
      slots,
      season: SEASONS.find(s => s.fr === season) ?? {
        fr: season,
        en: season
      },
      ageRange: ageMax ? `${ageMin} – ${ageMax}` : `${ageMin} +`,
      capacity: Number(capacity),
      registered: activity?.registered ?? 0,
      waitlisted: activity?.waitlisted ?? 0,
      priceResident: Number(priceResident),
      priceNonResident: Number(priceNonResident || Number(priceResident) * 2),
      status: activity?.status ?? 'open',
      visible,
      prerequisites,
      resources
    });
  };
  const field = 'w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#4F46E5] focus:border-[#4F46E5]';
  const labelClass = 'block text-xs font-medium text-slate-600 mb-1';
  return <div className="space-y-4 animate-in fade-in duration-200" data-id="element-2110" data-mp="129ejrxnw7m5h">
      <button onClick={onCancel} className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-[#4F46E5] transition-colors" data-id="element-2111" data-mp="176o9zfj3w1ud">
        <ArrowLeftIcon className="w-4 h-4" data-id="element-2112" data-mp="1zvvi968d45st" />
        {t('Retour aux activités', 'Back to activities')}
      </button>

      <div className="bg-white rounded-xl border border-slate-200 p-6" data-id="element-2113" data-mp="1l78uqh8l07qi">
        <h2 className="text-xl font-bold text-[#0F172A] font-heading" data-id="element-2114" data-mp="1l54p9uv1b0o4">
          {editing ? t('Modifier l’activité', 'Edit activity') : t('Nouvelle activité', 'New activity')}
        </h2>
        <p className="text-sm text-slate-500 mt-1" data-id="element-2115" data-mp="2imcw64ojcwu4">
          {t('Les citoyens verront cette activité dans le catalogue bciti+ dès qu’elle sera visible.', 'Citizens will see this activity in the bciti+ catalogue as soon as it is visible.')}
        </p>
      </div>

      {/* Identity */}
      <section className="bg-white rounded-xl border border-slate-200 p-6" data-id="element-2116" data-mp="gxgrd2v2g403">
        <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4" data-id="element-2117" data-mp="bycjcfjvocbc">
          {t('Identification', 'Identification')}
        </h3>

        <div className="mb-4" data-id="element-2118" data-mp="3kefqmk72vyz5">
          <span className={labelClass} data-id="element-2119" data-mp="3kfqtmhsp92p7">{t('Type d’activité', 'Activity type')}</span>
          <div className="flex flex-wrap gap-2 mb-2" data-id="element-2120" data-mp="3fgxhott29jr5">
            {known.map(k => <button key={k.fr} onClick={() => {
            setTypeFr(k.fr);
            setTypeEn(k.en);
          }} className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${typeFr === k.fr ? 'bg-[#4F46E5] text-white border-[#4F46E5]' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`} data-id="element-2121" data-mp="3tfx4rx0xdql7">
                {lang === 'fr' ? k.fr : k.en}
              </button>)}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-2122" data-mp="1zprurbpdjfnm">
            <input type="text" value={typeFr} onChange={e => setTypeFr(e.target.value)} placeholder={t('Type en français', 'Type in French')} aria-label={t('Type en français', 'Type in French')} className={field} data-id="element-2123" data-mp="1usi0nir1iftg" />
            <input type="text" value={typeEn} onChange={e => setTypeEn(e.target.value)} placeholder={t('Type en anglais', 'Type in English')} aria-label={t('Type en anglais', 'Type in English')} className={field} data-id="element-2124" data-mp="1gt8jogv0fhof" />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4" data-id="element-2125" data-mp="3652auocrjarz">
          <div data-id="element-2126" data-mp="3dpv3mh2820n1">
            <label htmlFor="act-name-fr" className={labelClass} data-id="element-2127" data-mp="3dqe9gzelkykp">
              {t('Nom de l’activité (français)', 'Activity name (French)')}
            </label>
            <input id="act-name-fr" type="text" value={nameFr} onChange={e => setNameFr(e.target.value)} placeholder={t('Natation — Junior', 'Swimming — Junior')} className={field} data-id="element-2128" data-mp="3ng7lg1injfph" />
          </div>
          <div data-id="element-2129" data-mp="2zq3h1rxn3bxf">
            <label htmlFor="act-name-en" className={labelClass} data-id="element-2130" data-mp="2zry5vhc17o6s">
              {t('Nom de l’activité (anglais)', 'Activity name (English)')}
            </label>
            <input id="act-name-en" type="text" value={nameEn} onChange={e => setNameEn(e.target.value)} placeholder={t('Swimming — Junior', 'Swimming — Junior')} className={field} data-id="element-2131" data-mp="38rbey8w8uaqz" />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-2132" data-mp="2l1gs0avmdjl3">
          <div data-id="element-2133" data-mp="2l42wg1n7riz3">
            <label htmlFor="act-venue" className={labelClass} data-id="element-2134" data-mp="2g2vqm84mstv2">
              {t('Lieu', 'Location')}
            </label>
            <select id="act-venue" value={venueId} onChange={e => setVenueId(e.target.value)} className={field} data-id="element-2135" data-mp="2u36gb7u7g2en">
              {FACILITIES.map(f => <option key={f.id} value={f.id} data-id="element-2136" data-mp="21esm7368lq8d">
                  {f.name[lang]}
                </option>)}
            </select>
            <p className="text-xs text-slate-400 mt-1" data-id="element-2137" data-mp="1xakvj0g7y07i">
              {t('Occupation actuelle : ', 'Current occupancy: ')}
              {venue.occupancy} %
            </p>
          </div>
          <div data-id="element-2138" data-mp="26azj66gnnre6">
            <label htmlFor="act-season" className={labelClass} data-id="element-2139" data-mp="1inhvam0ihdgh">
              {t('Session', 'Season')}
            </label>
            <select id="act-season" value={season} onChange={e => setSeason(e.target.value)} className={field} data-id="element-2140" data-mp="1dp6m8di1j6zb">
              {SEASONS.map(s => <option key={s.fr} value={s.fr} data-id="element-2141" data-mp="3u3a26vnjalu5">
                  {s[lang]}
                </option>)}
            </select>
          </div>
        </div>
      </section>

      {/* Schedule */}
      <section className="bg-white rounded-xl border border-slate-200 p-6" data-id="element-2142" data-mp="3lay91211ckah">
        <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4" data-id="element-2143" data-mp="cu3jeo990wec">
          {t('Horaires', 'Schedule')}
        </h3>

        <div className="flex gap-2 mb-4" data-id="element-2144" data-mp="gv5hh1rfj2eq">
          {[{
          id: 'weekly' as const,
          icon: RepeatIcon,
          label: t('Récurrente', 'Recurring'),
          hint: t('chaque semaine', 'every week')
        }, {
          id: 'once' as const,
          icon: CalendarIcon,
          label: t('Séance unique', 'One-time'),
          hint: t('une seule date', 'a single date')
        }].map(option => {
          const Icon = option.icon;
          const active = recurrence === option.id;
          return <button key={option.id} onClick={() => setRecurrence(option.id)} className={`flex-1 flex items-start gap-3 px-4 py-3 rounded-lg border text-left transition-colors ${active ? 'border-[#4F46E5] bg-indigo-50/60 ring-1 ring-[#4F46E5]/20' : 'border-slate-200 hover:bg-slate-50'}`} data-id="element-2145" data-mp="2z3vuhpxsahnf">
                <Icon className={`w-4 h-4 mt-0.5 ${active ? 'text-[#4F46E5]' : 'text-slate-400'}`} data-id="element-2146" data-mp="3hrhdl8xep04i" />
                <span data-id="element-2147" data-mp="26dvotovgl0ji">
                  <span className="block text-sm font-medium text-slate-800" data-id="element-2148" data-mp="26ef5715gngvf">
                    {option.label}
                  </span>
                  <span className="block text-xs text-slate-500" data-id="element-2149" data-mp="21dr6dtazpvqn">
                    {option.hint}
                  </span>
                </span>
              </button>;
        })}
        </div>

        <ul className="space-y-2" data-id="element-2150" data-mp="1w847j2c0anrr">
          {slots.map((slot, index) => <li key={slot.id} className="flex flex-wrap items-end gap-3 rounded-lg border border-slate-200 px-4 py-3" data-id="element-2151" data-mp="217qkdanxa201" data-mpi={index}>
              {recurrence === 'weekly' ? <div className="min-w-[140px]" data-id="element-2152" data-mp="2a1dbuol3kj3f">
                  <span className={labelClass} data-id="element-2153" data-mp="2f06qwlg18i5p">{t('Jour', 'Day')}</span>
                  <select value={slot.weekday} aria-label={t('Jour', 'Day')} onChange={e => setSlots(prev => prev.map((s, i) => i === index ? {
              ...s,
              weekday: Number(e.target.value)
            } : s))} className={field} data-id="element-2154" data-mp="2f2j5qpqhucej">
                    {WEEKDAYS.map(d => <option key={d.value} value={d.value} data-id="element-2155" data-mp="hmdqg4p35zbr">
                        {lang === 'fr' ? d.fr : d.en}
                      </option>)}
                  </select>
                </div> : <div className="min-w-[160px]" data-id="element-2156" data-mp="rlcn0owku4ie">
                  <span className={labelClass} data-id="element-2157" data-mp="15i9hr1ftg64k">{t('Date', 'Date')}</span>
                  <input type="date" value={slot.date ?? ''} aria-label={t('Date', 'Date')} onChange={e => setSlots(prev => prev.map((s, i) => i === index ? {
              ...s,
              date: e.target.value,
              weekday: e.target.value ? new Date(`${e.target.value}T12:00:00`).getDay() || 7 : s.weekday
            } : s))} className={field} data-id="element-2158" data-mp="10k7vk7n3fqov" />
                </div>}

              <div className="w-28" data-id="element-2159" data-mp="1k5q67v95tis9">
                <span className={labelClass} data-id="element-2160" data-mp="1k4otzngeqq8y">{t('Début', 'Start')}</span>
                <input type="time" value={slot.startTime} aria-label={t('Heure de début', 'Start time')} onChange={e => setSlots(prev => prev.map((s, i) => i === index ? {
              ...s,
              startTime: e.target.value
            } : s))} className={field} data-id="element-2161" data-mp="1owzxf0acwu69" />
              </div>

              <div className="w-28" data-id="element-2162" data-mp="1lnq7rmkw90xl">
                <span className={labelClass} data-id="element-2163" data-mp="1lmoynnnfuoii">{t('Fin', 'End')}</span>
                <input type="time" value={slot.endTime} aria-label={t('Heure de fin', 'End time')} onChange={e => setSlots(prev => prev.map((s, i) => i === index ? {
              ...s,
              endTime: e.target.value
            } : s))} className={field} data-id="element-2164" data-mp="1godotcx58e0a" />
              </div>

              {toDecimal(slot.endTime) <= toDecimal(slot.startTime) && <p className="flex items-center gap-1.5 text-xs text-red-600 pb-2" data-id="element-2165" data-mp="sx84akg9cxjn">
                  <AlertTriangleIcon className="w-3.5 h-3.5" data-id="element-2166" data-mp="ozk7tcyx2wpn" />
                  {t('L’heure de fin doit suivre le début.', 'The end time must follow the start.')}
                </p>}

              {slots.length > 1 && <button onClick={() => setSlots(prev => prev.filter((_, i) => i !== index))} aria-label={t('Retirer ce créneau', 'Remove this slot')} className="ml-auto p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors" data-id="element-2167" data-mp="2qmn6d0lor7c7">
                  <TrashIcon className="w-4 h-4" data-id="element-2168" data-mp="27xgiz09y5rk2" />
                </button>}
            </li>)}
        </ul>

        <button onClick={() => setSlots(prev => [...prev, {
        id: `sl-${Date.now()}`,
        weekday: 4,
        startTime: '18:00',
        endTime: '19:00'
      }])} className="inline-flex items-center gap-2 mt-3 px-3.5 py-2 text-sm font-medium text-[#4F46E5] bg-indigo-50 border border-indigo-100 rounded-lg hover:bg-indigo-100 transition-colors" data-id="element-2169" data-mp="2cvqcl6g0wk3k">
          <PlusIcon className="w-4 h-4" data-id="element-2170" data-mp="10lrvy2fulw72" />
          {t('Ajouter un créneau', 'Add a time slot')}
        </button>

        {conflicts.length > 0 && <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50/70 px-4 py-3" data-id="element-2171" data-mp="19m7yn9orozav">
            <p className="flex items-center gap-2 text-sm font-semibold text-amber-800 mb-1.5" data-id="element-2172" data-mp="1emcf4zpodu13">
              <AlertTriangleIcon className="w-4 h-4" data-id="element-2173" data-mp="hvtljfokvr09" />
              {t('Le lieu est déjà occupé sur ces plages', 'The venue is already busy on these slots')}
            </p>
            <ul className="space-y-1" data-id="element-2174" data-mp="qv6zakjqj1qs">
              {conflicts.map(conflict => <li key={conflict} className="text-xs text-amber-800" data-id="element-2175" data-mp="vn881whp2ac2">
                  • {conflict}
                </li>)}
            </ul>
            <p className="text-xs text-amber-700/80 mt-2" data-id="element-2176" data-mp="3uavxr4xifafi">
              {t('Vous pouvez enregistrer malgré tout — la réservation du plateau sera à confirmer.', 'You can still save — the facility booking will need confirmation.')}
            </p>
          </div>}
      </section>

      {/* Resources */}
      <ActivityResources slots={slots} venue={venue} venueConflicts={conflicts} existing={existing} activityId={activity?.id} selected={resources} onChange={setResources} data-id="element-2177" data-mp="3htbgv7m6ldbe" />

      {/* Capacity and pricing */}
      <section className="bg-white rounded-xl border border-slate-200 p-6" data-id="element-2178" data-mp="3437veiv4lnn0">
        <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4" data-id="element-2179" data-mp="3414xlcnqeq7q">
          {t('Capacité et tarifs', 'Capacity and pricing')}
        </h3>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-id="element-2180" data-mp="2u63cqt0hoaay">
          <div data-id="element-2181" data-mp="2q3op7vlvkmdc">
            <label htmlFor="act-age-min" className={labelClass} data-id="element-2182" data-mp="2q258y507ybbk">
              {t('Âge minimum', 'Minimum age')}
            </label>
            <input id="act-age-min" type="number" min="0" value={ageMin} onChange={e => setAgeMin(e.target.value)} className={field} data-id="element-2183" data-mp="2l4vluw0aybqw" />
          </div>
          <div data-id="element-2184" data-mp="21itqjjx7bh5s">
            <label htmlFor="act-age-max" className={labelClass} data-id="element-2185" data-mp="21jbwj89o19qw">
              {t('Âge maximum', 'Maximum age')}
            </label>
            <input id="act-age-max" type="number" min="0" value={ageMax} onChange={e => setAgeMax(e.target.value)} placeholder={t('aucun', 'none')} className={field} data-id="element-2186" data-mp="26hn4tekbbc70" />
          </div>
          <div data-id="element-2187" data-mp="2kgwd7s2a120f">
            <label htmlFor="act-capacity" className={labelClass} data-id="element-2188" data-mp="2kfbseu5rkxfr">
              {t('Places', 'Spots')}
            </label>
            <input id="act-capacity" type="number" min="1" value={capacity} onChange={e => setCapacity(e.target.value)} className={field} data-id="element-2189" data-mp="3d63kpgjpl1xt" />
          </div>
          <div data-id="element-2190" data-mp="16fqhqqz530qw">
            <label htmlFor="act-price-res" className={labelClass} data-id="element-2191" data-mp="16fgtb7tqt1nb">
              {t('Tarif résident ($)', 'Resident rate ($)')}
            </label>
            <input id="act-price-res" type="number" min="0" value={priceResident} onChange={e => setPriceResident(e.target.value)} className={field} data-id="element-2192" data-mp="1fa4vuq7xo58f" />
          </div>
          <div data-id="element-2193" data-mp="ijm4zdy80gba">
            <label htmlFor="act-price-non" className={labelClass} data-id="element-2194" data-mp="ik5navrs96pu">
              {t('Tarif non-résident ($)', 'Non-resident rate ($)')}
            </label>
            <input id="act-price-non" type="number" min="0" value={priceNonResident} onChange={e => setPriceNonResident(e.target.value)} placeholder={priceResident ? String(Number(priceResident) * 2) : undefined} className={field} data-id="element-2195" data-mp="wkou5zkii6qf" />
            <p className="text-xs text-slate-400 mt-1" data-id="element-2196" data-mp="2yaxm0mjnplku">
              {t('Double du tarif résident par défaut.', 'Twice the resident rate by default.')}
            </p>
          </div>
        </div>
      </section>

      {/* Prerequisites */}
      <section className="bg-white rounded-xl border border-slate-200 p-6" data-id="element-2197" data-mp="38a6d97n50m3i">
        <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-1" data-id="element-2198" data-mp="3d87ynz81cxi5">
          {t('Préalables', 'Prerequisites')}
        </h3>
        <p className="text-sm text-slate-500 mb-4" data-id="element-2199" data-mp="3m73253jp3thr">
          {t('Un préalable bloquant empêche l’inscription ; sinon, il affiche un avertissement.', 'A blocking prerequisite prevents registration; otherwise it shows a warning.')}
        </p>
        <ul className="space-y-2" data-id="element-2200" data-mp="4r3dlicfqa8x">
          {PREREQUISITE_CATALOGUE.map(item => {
          const picked = prerequisites.find(p => p.id === item.id);
          return <li key={item.id} className={`flex items-start gap-3 rounded-lg border px-4 py-3 transition-colors ${picked ? 'border-[#4F46E5]/40 bg-indigo-50/40' : 'border-slate-200'}`} data-id="element-2201" data-mp="8qlzagc2ypct">
                <button onClick={() => setPrerequisites(prev => picked ? prev.filter(p => p.id !== item.id) : [...prev, item])} role="checkbox" aria-checked={Boolean(picked)} aria-label={item.label[lang]} className={`mt-0.5 w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${picked ? 'bg-[#4F46E5] border-[#4F46E5] text-white' : 'bg-white border-slate-300 hover:border-[#4F46E5]'}`} data-id="element-2202" data-mp="1kbvshxuuy98y">
                  {picked && <CheckIcon className="w-3.5 h-3.5" data-id="element-2203" data-mp="p1h6rzfme0ec" />}
                </button>
                <div className="min-w-0 flex-1" data-id="element-2204" data-mp="t1hjnfn2m3ja">
                  <p className="text-sm font-medium text-slate-800" data-id="element-2205" data-mp="t4dj58ekh09u">
                    {item.label[lang]}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5" data-id="element-2206" data-mp="k9pcpex99cjg">
                    {item.detail[lang]}
                  </p>
                </div>
                {picked && <button onClick={() => setPrerequisites(prev => prev.map(p => p.id === item.id ? {
              ...p,
              blocking: !p.blocking
            } : p))} className={`shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold border transition-colors ${picked.blocking ? 'bg-indigo-50 text-[#4F46E5] border-indigo-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`} data-id="element-2207" data-mp="ac1hknvqm30r">
                    {picked.blocking ? <>
                        <LockIcon className="w-3 h-3" data-id="element-2208" data-mp="2emfv66wbpj8o" />
                        {t('Bloquant', 'Blocking')}
                      </> : <>
                        <AlertTriangleIcon className="w-3 h-3" data-id="element-2209" data-mp="2oiraz6v4gr7v" />
                        {t('Avertissement', 'Warning')}
                      </>}
                  </button>}
              </li>;
        })}
        </ul>
      </section>

      {/* Visibility + actions */}
      <section className="bg-white rounded-xl border border-slate-200 p-6" data-id="element-2210" data-mp="2tk88qtib7xk8">
        <div className="flex items-start justify-between gap-4 flex-wrap" data-id="element-2211" data-mp="135pm8g0imdbd">
          <div className="flex items-start gap-3" data-id="element-2212" data-mp="z55opbc157op">
            {visible ? <EyeIcon className="w-5 h-5 text-[#4F46E5] mt-0.5" data-id="element-2213" data-mp="z78ouo6wch75" /> : <EyeOffIcon className="w-5 h-5 text-slate-400 mt-0.5" data-id="element-2214" data-mp="1npkfo6ww57et" />}
            <div data-id="element-2215" data-mp="1sncgjqfik3v5">
              <p className="text-sm font-medium text-slate-800" data-id="element-2216" data-mp="1smsxg6e4n9fj">
                {visible ? t('Visible dans le catalogue citoyen', 'Visible in the citizen catalogue') : t('Masquée du catalogue citoyen', 'Hidden from the citizen catalogue')}
              </p>
              <p className="text-xs text-slate-500 mt-0.5" data-id="element-2217" data-mp="14ssx5zp6awbt">
                {visible ? t('Les citoyens peuvent s’inscrire dès l’ouverture de la session.', 'Citizens can register as soon as the session opens.') : t('L’activité reste interne — seuls les employés peuvent inscrire quelqu’un.', 'The activity stays internal — only staff can register someone.')}
              </p>
            </div>
          </div>
          <button type="button" role="switch" aria-checked={visible} aria-label={t('Visibilité', 'Visibility')} onClick={() => setVisible(v => !v)} className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:ring-offset-1 ${visible ? 'bg-[#4F46E5]' : 'bg-slate-200'}`} data-id="element-2218" data-mp="348i95o5u2gan">
            <span aria-hidden="true" className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition duration-200 ${visible ? 'translate-x-4' : 'translate-x-0'}`} data-id="element-2219" data-mp="2gmbr092p3k6p" />
          </button>
        </div>
      </section>

      <div className="sticky bottom-0 flex items-center justify-between gap-3 flex-wrap bg-white/95 backdrop-blur border border-slate-200 rounded-xl px-5 py-4" data-id="element-2220" data-mp="5s7jm9ptxqv5">
        <p className="text-xs text-slate-500" data-id="element-2221" data-mp="v3289m76k3l5">
          {complete ? t('Prêt à être enregistré.', 'Ready to be saved.') : t('Renseignez le type, le nom, les places et le tarif résident.', 'Fill in the type, name, spots and resident rate.')}
        </p>
        <div className="flex items-center gap-2" data-id="element-2222" data-mp="c6sn6exv2crn">
          <button onClick={onCancel} className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors" data-id="element-2223" data-mp="c7kj5qvdecig">
            {t('Annuler', 'Cancel')}
          </button>
          <button onClick={submit} disabled={!complete} className="inline-flex items-center gap-2 px-4 py-2 bg-[#4F46E5] hover:bg-[#4338CA] text-white text-sm font-semibold rounded-lg transition-colors shadow-sm disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-2224" data-mp="h39d9eomf17d">
            <CheckIcon className="w-4 h-4" data-id="element-2225" data-mp="3agb60uyqmv51" />
            {editing ? t('Enregistrer les modifications', 'Save changes') : t('Créer l’activité', 'Create activity')}
          </button>
        </div>
      </div>
    </div>;
}